import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { AccountDummyDetails } from '../hooks/useAccountDetails';
import { StatusBadge } from '../../../../../../shared/components/ui/StatusBadge';
import type { StatusBadgeProps } from '../../../../../../shared/components/ui/StatusBadge';

interface Props {
  details: AccountDummyDetails;
}

interface InfoRowProps {
  label: string;
  value: string;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: '18px',
      letterSpacing: '0.05em',
      color: '#434656',
      textTransform: 'uppercase',
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 3, py: '10px', borderBottom: '1px solid #f1f5f9' }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#64748b', minWidth: 180 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#0b1c30' }}>{value}</Typography>
  </Box>
);

const paymentStatusVariant = (status: string): NonNullable<StatusBadgeProps['status']> => {
  if (status === 'active') return 'active';
  if (status === 'failed') return 'revoked';
  if (status === 'past_due') return 'suspended';
  return 'neutral';
};

const invoiceStatusVariant = (status: string): NonNullable<StatusBadgeProps['status']> => {
  if (status === 'paid') return 'active';
  if (status === 'open') return 'suspended';
  if (status === 'void') return 'revoked';
  return 'neutral';
};

const cardSx = {
  border: '1px solid #E2E8F0',
  borderRadius: '12px',
  p: 3,
  bgcolor: '#ffffff',
  boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
};

const tableCellHeaderSx = {
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #E2E8F0',
  py: '12px',
};

const tableCellSx = {
  fontSize: '14px',
  color: '#0b1c30',
  py: '12px',
  borderBottom: '1px solid #f1f5f9',
};

export const AccountDetailsBillingTab: React.FC<Props> = ({ details }) => {
  const nextInvoice = `${details.currency} ${details.nextInvoiceAmount} on ${details.nextInvoiceDate}`;

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={cardSx}>
        <SectionTitle>Stripe</SectionTitle>
        <InfoRow label="Customer ID" value={details.stripeCustomerId} />
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            py: '10px',
            borderBottom: '1px solid #f1f5f9',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#64748b', minWidth: 180 }}>
            Payment Status
          </Typography>
          <StatusBadge
            label={details.paymentStatus}
            status={paymentStatusVariant(details.paymentStatus)}
          />
        </Box>
        <InfoRow label="Next Invoice" value={nextInvoice} />
        <InfoRow label="Dunning Attempts" value={String(details.dunningAttempts)} />
      </Box>

      <Box sx={cardSx}>
        <SectionTitle>Invoice History</SectionTitle>
        <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableCellHeaderSx}>Invoice ID</TableCell>
                <TableCell sx={tableCellHeaderSx}>Date</TableCell>
                <TableCell align="right" sx={tableCellHeaderSx}>
                  Amount
                </TableCell>
                <TableCell sx={tableCellHeaderSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.invoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: '#f8fafc' } }}
                >
                  <TableCell sx={tableCellSx}>{inv.id}</TableCell>
                  <TableCell sx={tableCellSx}>{inv.date}</TableCell>
                  <TableCell align="right" sx={tableCellSx}>
                    {details.currency} {inv.amount}
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    <StatusBadge label={inv.status} status={invoiceStatusVariant(inv.status)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};
